import React from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useMutation } from 'react-query';
import cn from '../../../../../utils/tools/cn';

// Services
import useMediaService from '../../../../../services/mediaService';

// Icons
import { ReactComponent as PlusIcon } from '../../../../../assets/icons/plus.svg';

// Components
import Button from '../../../../atoms/Button/Button';

import styles from './AddNewMedia.module.scss';

function AddNewMedia({
  setUploadErrors,
  setUploadSuccess,
  setMedias,
  displayLimit,
  buttonLabel,
  buttonTheme,
  handleAfterAdding,
  scrollToTopOnUpload,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const {
    postUploadFile, postMedia,
  } = useMediaService();

  const handleUploadError = (error, name) => {
    if (scrollToTopOnUpload) window.scrollTo(0, 0);

    const errorCode = error?.response?.data?.code;
    const postError = t(errorCode ? `code_errors.${errorCode}_media` : 'medias.upload.error', { name });

    setUploadErrors((state) => state.concat([postError]));
  };

  const {
    mutate: postMediaMutate,
    isLoading: postMediaLoading,
  } = useMutation(({ name, fileToken }) => postMedia({ name, fileToken }), {
    onSuccess: (res) => {
      if (res?.data) {
        if (scrollToTopOnUpload) window.scrollTo(0, 0);
        setMedias((state) => state.concat([res.data]));

        setUploadSuccess((state) => state.concat([t('medias.upload.success', { name: res.data.name })]));
        if (handleAfterAdding) handleAfterAdding();
      }
    },
    onError: (error, data) => {
      handleUploadError(error, data?.name);
    },
  });

  const formatMediaName = (name) => {
    if (!name) return '';
    const nameWithoutExt = name.split('.')?.[0];
    const ext = name.split('.')?.[1];
    return `${nameWithoutExt?.slice(0, 40)}.${ext}` || '';
  };

  const {
    mutate: postUploadFileMutate,
    isLoading: postUploadFileLoading,
  } = useMutation((file) => postUploadFile(file), {
    onSuccess: (res, data) => {
      if (res?.data?.token) {
        postMediaMutate({ name: formatMediaName(data.name), fileToken: res.data.token });
      }
    },
    onError: (error, data) => {
      handleUploadError(error, data);
    },
  });

  const handleUploadFiles = (files) => {
    setUploadErrors([]);
    setUploadSuccess([]);

    if (files?.length) {
      const maxErrors = [];

      Array.from(files).forEach((file) => {
        const maxSize = 1048576; // 1 MO

        if (file.size > maxSize) {
          maxErrors.push(t('medias.upload.max', { name: file.name }));
        } else {
          postUploadFileMutate(file);
        }
      });

      if (maxErrors?.length) {
        setUploadErrors((state) => state.concat(maxErrors));
      }
    }
  };

  return (
    <div className="d-flex d-col a-end">
      {displayLimit ? (
        <p className={cn([styles.limit, 'd-inline-flex m-b-10 fs-12'])}>
          {t('medias.upload.limit')}
        </p>
      ) : null}
      <label htmlFor="uploadMediaFile" className="p-relative c-pointer hover-bright mobile-w-100">
        <input
          name="uploadMediaFile"
          id="uploadMediaFile"
          type="file"
          onChange={(input) => handleUploadFiles(input.target.files)}
          className={styles.input_file}
          multiple
          disabled={postMediaLoading || postUploadFileLoading}
        />
        <Button
          className="pointer-none"
          isLoading={postMediaLoading || postUploadFileLoading}
          full={isMobile}
          theme={buttonTheme}
        >
          <PlusIcon className="m-r-10" />
          {buttonLabel}
        </Button>
      </label>
    </div>
  );
}

AddNewMedia.propTypes = {
  setUploadErrors: PropTypes.func,
  setUploadSuccess: PropTypes.func,
  setMedias: PropTypes.func,
  displayLimit: PropTypes.bool,
  buttonLabel: PropTypes.string.isRequired,
  buttonTheme: PropTypes.string,
  handleAfterAdding: PropTypes.func,
  scrollToTopOnUpload: PropTypes.bool,
};

AddNewMedia.defaultProps = {
  setUploadErrors: () => {},
  setUploadSuccess: () => {},
  setMedias: () => {},
  displayLimit: false,
  buttonTheme: null,
  handleAfterAdding: () => {},
  scrollToTopOnUpload: false,
};

export default AddNewMedia;
